import './style.scss';

import { Field, FormikErrors, FormikTouched } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Form, InputGroup, Row } from 'react-bootstrap-v5';
import { useTranslation } from 'react-i18next';

import { STEP_USERS, USER_TYPE } from '../../../../../../utils/common/constants';
import { SelectFilter } from '../../../../../../utils/common/models';
import { checkFullNameUser, checkValidFullPhoneNumber, checkValidPhoneNumber } from '../../../../../../utils/services/userService';
import { AdminResponse, City, Nationality, UserFormModel } from '../../../../models';
import { Housing } from '../../../../models/Nationality';
import { useAppDispatch, useAppSelector } from '../../../../../../../setup/redux/Hooks';
import { selectActionUserModal, selectProvinceData, selectHousingData, userActions, selectCurrentFilterProvinceId, selectCurrentFilterHousingId } from '../../../../redux/UserSlice';
import { CustomSelect } from '../../../../components/CustomSelect';
import { Province } from '../../../../../customers/models';

type Props = {
  className?: string,
  values: UserFormModel,
  rtl?: boolean,
  step?: number,
  errors: FormikErrors<UserFormModel>,
  touched: FormikTouched<UserFormModel>,
  nationalities?: Nationality[],
  cities?: City[],
  housings?: Housing[],
  avatar?: string,
  clearAvatar?: () => void,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  setFieldError: (field: string, value: any) => void,
  setTouched: (value: FormikTouched<UserFormModel>, shouldValidate?: boolean) => void,
  listAdmin?: AdminResponse[]
}

const AccountInfo: React.FC<Props> = ({ setFieldValue, setFieldError, setTouched, step, avatar = "", rtl, values, errors, cities, housings, touched, nationalities = [], clearAvatar, listAdmin }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const inputFile = useRef<HTMLInputElement | null>(null);
  const [newAvatarEmpl, setNewAvatarEmpl] = useState("");
  const [textErrorPhone, setTextErrorPhone] = useState("");
  const [isActive, setIsActive] = useState(values.isActive);
  const [selectedProvinceId, setSelectedProvinceId] = useState('');
  const [allHousings, setAllHousings] = useState<Housing[]>([]);
  const [lastHousingIdChecked, setLastHousingIdChecked] = useState<string | number | undefined>(undefined);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const hasSetInitialCityFromFilter = useRef(false);

  const isShowViewModalCreateUser = useAppSelector(selectActionUserModal);
  const provinces = useAppSelector(selectProvinceData);
  const housingList = useAppSelector(selectHousingData);
  const filterProvinceId = useAppSelector(selectCurrentFilterProvinceId);
  const filterHousingId = useAppSelector(selectCurrentFilterHousingId);

  // Fetch provinces on mount
  useEffect(() => {
    if (!provinces || provinces.length === 0) {
      dispatch(userActions.fetchProvinceData());
    }
  }, []);

  // Handle initial setup for create mode
  useEffect(() => {
    // Reset the ref when switching between create and update mode
    if (values.housingId) {
      // Update mode - reset the flag
      hasSetInitialCityFromFilter.current = false;
    } else {
      // Create mode - reset the flag so it can be set from filter
      hasSetInitialCityFromFilter.current = false;
    }

    // For create mode, fetch housing data if not available
    if (!values.housingId) {
      if (!housingList || housingList.length === 0) {
        dispatch(userActions.fetchHousingData());
      }

      // Set initial province from filter (for create mode)
      if (filterProvinceId) {
        setSelectedProvinceId(filterProvinceId);
        // Fetch housing for the filtered province
        dispatch(userActions.fetchHousingByProvinceData(filterProvinceId));
      }
    } else {
      // In update mode, if cityId is already set from API, ensure it's in the correct format
      if (values.cityId && !selectedProvinceId) {
        const cityIdStr = values.cityId.toString();
        setSelectedProvinceId(cityIdStr);
      }
    }
  }, [values.housingId, filterProvinceId]); // Run when housingId or filterProvinceId changes

  // Set province based on existing housing when editing a worker
  useEffect(() => {
    if (!values.housingId) return;

    // Try to find in current housingList first
    let existingHousing = housingList?.find((h: Housing) => h.id?.toString() === values.housingId?.toString());

    // If not found, try allHousings
    if (!existingHousing && allHousings.length > 0) {
      existingHousing = allHousings.find((h: Housing) => h.id?.toString() === values.housingId?.toString());
    }

    // If still not found and we haven't fetched for this housingId yet, fetch all housings once
    if (!existingHousing && lastHousingIdChecked !== values.housingId) {
      setLastHousingIdChecked(values.housingId);
      // Only fetch if we don't have the housing in our lists
      // Check if housingList might have it but we just haven't found it yet (e.g., if it's filtered)
      const hasHousingInList = housingList.some((h: Housing) => h.id?.toString() === values.housingId?.toString());
      if (!hasHousingInList) {
        dispatch(userActions.fetchHousingData());
      }
      return; // Will retry when housingList is populated
    }

    // If we found the housing, set the province
    if (existingHousing && existingHousing.provinceId) {
      const provId = existingHousing.provinceId.toString();
      // In update mode, always set the province from the housing (override filter province)
      if (selectedProvinceId !== provId) {
        setSelectedProvinceId(provId);
        // Set cityId based on province - ensure it's a string to match province options
        const cityIdValue = provId;
        // Only set if cityId is not already set or is different
        if (!values.cityId || values.cityId.toString() !== cityIdValue) {
          setFieldValue('cityId', cityIdValue, false);
        }
        // Fetch housings for this province to populate the housing dropdown
        dispatch(userActions.fetchHousingByProvinceData(provId));
        // Mark that cityId has been set from housing, so initial load is complete
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      }
    }
  }, [values.housingId, housingList, allHousings, selectedProvinceId, lastHousingIdChecked, dispatch, setFieldValue, isInitialLoad]);

  useEffect(() => {
    if (filterHousingId && values.userType === USER_TYPE.WORKER && !values.housingId) {
      setFieldValue('housingId', filterHousingId);
    }
  }, [values.userType, filterHousingId]);

  // Set cityId from filter when modal opens (for create mode) - only once on initial load
  useEffect(() => {
    // Only set in create mode (when housingId doesn't exist) and when filterProvinceId is available
    // Only set once on initial load, not when user manually changes city
    if (!values.housingId && filterProvinceId && values.userType === USER_TYPE.WORKER && !hasSetInitialCityFromFilter.current) {
      // Only set if cityId is not already set
      if (!values.cityId) {
        setFieldValue('cityId', filterProvinceId, false);
        setSelectedProvinceId(filterProvinceId);
        hasSetInitialCityFromFilter.current = true;
      } else {
        // If cityId is already set, mark as initialized
        hasSetInitialCityFromFilter.current = true;
      }
    }
  }, [filterProvinceId, values.userType, values.housingId, values.cityId, setFieldValue]);

  // Set city value when user type becomes DRIVER/TECHNICIAN and filterProvinceId is available
  useEffect(() => {
    if (filterProvinceId && (values.userType === USER_TYPE.DRIVER || values.userType === USER_TYPE.TECHNICIAN) && !values.cityId) {
      setFieldValue('cityId', filterProvinceId);
      dispatch(userActions.fetchHousingByProvinceData(filterProvinceId));
    }
  }, [values.userType, filterProvinceId]);

  // Keep track of all housings to preserve selected housing when filtering by province
  useEffect(() => {
    if (housingList && housingList.length > 0) {
      setAllHousings(prevAll => {
        const newHousings = housingList.filter((h: Housing) => !prevAll.some(a => a.id === h.id));
        return [...prevAll, ...newHousings];
      });
    }
  }, [housingList, values.housingId]);

  // Watch for cityId changes and fetch housing list when city changes
  // This ensures the housing dropdown updates when city is changed in update mode
  useEffect(() => {
    // Skip during initial load - let the other useEffect handle that
    if (isInitialLoad) return;
    
    // Only fetch if cityId has a value and matches selectedProvinceId
    if (values.cityId && values.cityId === selectedProvinceId) {
      dispatch(userActions.fetchHousingByProvinceData(values.cityId));
    }
  }, [values.cityId, selectedProvinceId, isInitialLoad, dispatch]);

  // Handle province change - fetch housing filtered by province
  const handleProvinceChange = (provinceId: string) => {
    setSelectedProvinceId(provinceId);
    setIsInitialLoad(false); // Mark that user has manually changed the city

    // Update cityId in Formik for submission
    setFieldValue('cityId', provinceId || '');

    // Reset housing selection when province changes
    setFieldValue('housingId', '');

    if (provinceId) {
      dispatch(userActions.fetchHousingByProvinceData(provinceId));
    } else {
      dispatch(userActions.fetchHousingData());
    }
  };


  // Ensure selected housing is always in the options, even if filtered by province
  const getHousingOptions = () => {
    // If no province is selected, return empty list (excluding the currently selected housing if any)
    const isWorker = values.userType === USER_TYPE.WORKER;
    const isDriverOrTech = values.userType === USER_TYPE.DRIVER || values.userType === USER_TYPE.TECHNICIAN;

    const currentHousings = housingList || [];

    // In update mode: if housingId exists but cityId doesn't, allow showing the selected housing
    // This handles the case where cityId is being set asynchronously from housing's provinceId
    if (values.housingId && !values.cityId) {
      const selectedHousing = allHousings.find((h: Housing) => h.id?.toString() === values.housingId?.toString());
      if (selectedHousing) {
        return [selectedHousing];
      }
      // If not in allHousings yet, try to find in currentHousings
      const foundInCurrent = currentHousings.find((h: Housing) => h.id?.toString() === values.housingId?.toString());
      if (foundInCurrent) {
        return [foundInCurrent];
      }
      // Return empty to allow the useEffect to fetch and set cityId
      return [];
    }

    // For create mode: require cityId to be selected
    if (isWorker && !values.cityId) return [];
    if (isDriverOrTech && !values.cityId) return [];

    // If there's a selected housing and it's not in the current list, add it from allHousings
    if (values.housingId) {
      const isInCurrentList = currentHousings.some((h: Housing) => h.id?.toString() === values.housingId?.toString());
      if (!isInCurrentList) {
        const selectedHousing = allHousings.find((h: Housing) => h.id?.toString() === values.housingId?.toString());
        if (selectedHousing) {
          return [selectedHousing, ...currentHousings];
        }
      }
    }

    return currentHousings;
  };

  const changeAvatar = (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    const file: Blob = event.target.files[0];
    if (!file) return;
    setNewAvatarEmpl(file ? URL.createObjectURL(file) : '');
    setFieldValue("avatar", file);
    setFieldValue("isRemoveAvatar", false)
    clearAvatar?.();
  }

  const removeAvatar = () => {
    setFieldValue("avatar", "")
    setFieldValue("isRemoveAvatar", true)
    clearAvatar?.();
    setNewAvatarEmpl("");
  }

  const handlePhoneChange = (
    values: UserFormModel,
    setFieldError: (field: string, message: string | undefined) => void
  ) => {
    // if (!values || !values.phone || values.phone == "") {
    //   setTextErrorPhone("");
    //   return;
    // }
    // if (checkValidFullPhoneNumber(values?.phone ? values.phone : "")) {
    //   setTextErrorPhone("");
    // } else setTextErrorPhone(t('USERS_PAGE.CONTENT_STEP.VALIDATE_STEP.REQUIRE_PHONE'));
  }

  const handleChangeStatus = () => {
    var newValue = !isActive;
    setIsActive(newValue);
    setFieldValue("isActive", newValue);
  }
  return (
    <>
      <div className='box-step-2'>
        <Row className='flex-root step-1 m-auto'>
          {/* Avatar user */}
          <Col sm={12} className='mt-8'>
            <div className='group-avatar'>
              <div className='text'>
                <div>{t('USERS_PAGE.CONTENT_STEP.STEP2.FIELD_USER_IMAGE')}</div>
                <div className='text-grey'>{t('USERS_PAGE.CONTENT_STEP.STEP2.IMAGE_DESC')}</div>
              </div>
              <div className='upload'>
                <div className='circle' >
                  {!newAvatarEmpl && !avatar && <img src={'/assets/images/icons/user-avatar-employer.svg'}></img>}
                  {avatar ? <img className='image-user' src={avatar}></img> : newAvatarEmpl ? <img className='image-user' src={newAvatarEmpl}></img> : <></>}

                  <div className="icon-action icon-delete" onClick={removeAvatar}>
                    <i className="fas fa-trash"></i>
                  </div>
                  <div className="icon-action icon-edit" onClick={() => inputFile?.current && inputFile.current.click()}>
                    <i className="fas fa-edit"></i>
                  </div>
                </div>
                <input id="fileInput" type="file" ref={inputFile} accept="image/png, image/jpeg" className='none'
                  onClick={(event: any) => { event.target.value = null }} onChange={changeAvatar} />
              </div>
            </div>
          </Col>
          {values.userType == USER_TYPE.WORKER ? <>
            <Col sm={12} md={6} className='mt-8'>
              <Row>
                <Col sm={12} md={6} >
                  <InputGroup className='h-50px'>
                    <InputGroup.Text id='basic-addon1'>
                      <img src='/assets/images/icons/user.svg'></img>
                    </InputGroup.Text>
                    <Field
                      className='form-control h-50px'
                      name='firstNameEn'
                      type="text"
                      placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.PLACEHOLDER_FIRSTNAME_EN')}
                      value={values?.firstNameEn}
                    />
                  </InputGroup>
                  {errors.firstNameEn && touched.firstNameEn ? (
                    <div className='small text-danger'>{errors.firstNameEn}</div>
                  ) : null}
                </Col>
                <Col sm={12} md={6} className="mt-md-0 mt-8">
                  <InputGroup className='h-50px'>
                    <InputGroup.Text id='basic-addon1'>
                      <img src='/assets/images/icons/user.svg'></img>
                    </InputGroup.Text>
                    <Field
                      className='form-control h-50px'
                      name='lastNameEn'
                      type="text"
                      placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.PLACEHOLDER_LASTNAME_EN')}
                      value={values?.lastNameEn}
                    />
                  </InputGroup>
                  {errors.lastNameEn && touched.lastNameEn ? (
                    <div className='small text-danger'>{errors.lastNameEn}</div>
                  ) : null}
                </Col>
              </Row>
            </Col>
            <Col sm={12} md={6} className='mt-0 mt-md-8'>
              <Row>
                <Col sm={12} md={6} className="mt-md-0 mt-8">
                  <InputGroup className='h-50px'>
                    <InputGroup.Text id='basic-addon1'>
                      <img src='/assets/images/icons/user.svg'></img>
                    </InputGroup.Text>
                    <Field
                      className='form-control h-50px'
                      name='firstNameAr'
                      type="text"
                      placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.PLACEHOLDER_FIRSTNAME_AR')}
                      value={values?.firstNameAr}
                    />
                  </InputGroup>
                  {errors.firstNameAr && touched.firstNameAr ? (
                    <div className='small text-danger'>{errors.firstNameAr}</div>
                  ) : null}
                </Col>
                <Col sm={12} md={6} className="mt-md-0 mt-8">
                  <InputGroup className='h-50px'>
                    <InputGroup.Text id='basic-addon1'>
                      <img src='/assets/images/icons/user.svg'></img>
                    </InputGroup.Text>
                    <Field
                      className='form-control h-50px'
                      name='lastNameAr'
                      type="text"
                      placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.PLACEHOLDER_LASTNAME_AR')}
                      value={values?.lastNameAr}
                    />
                  </InputGroup>
                  {errors.lastNameAr && touched.lastNameAr ? (
                    <div className='small text-danger'>{errors.lastNameAr}</div>
                  ) : null}
                </Col>
              </Row>
            </Col>
          </> : <>
            {/* Full Name En */}
            <Col sm={12} md={6} className='mt-8'>
              <InputGroup className='h-50px'>
                <InputGroup.Text id='basic-addon1'>
                  <img src='/assets/images/icons/user.svg'></img>
                </InputGroup.Text>
                <Field
                  className='form-control h-50px'
                  name='fullNameEn'
                  type="text"
                  placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.PLACEHOLDER_FULLNAME_EN')}
                  value={values?.fullNameEn}
                />
              </InputGroup>
              {errors.fullNameEn && touched.fullNameEn ? (
                <div className='small text-danger'>{errors.fullNameEn}</div>
              ) : null}
              {!checkFullNameUser(values?.fullNameEn) && !errors.fullNameEn && isShowViewModalCreateUser &&
                <div className='small text-danger'>{t('USERS_PAGE.CONTENT_STEP.VALIDATE_STEP.VALIDATE_FULLNAME')}</div>
              }
            </Col>
            {/* Full Name Ar */}
            <Col sm={12} md={6} className='mt-8'>
              <InputGroup className='h-50px'>
                <InputGroup.Text id='basic-addon1'>
                  <img src='/assets/images/icons/user.svg'></img>
                </InputGroup.Text>
                <Field
                  className='form-control h-50px'
                  name='fullNameAr'
                  type="text"
                  placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.PLACEHOLDER_FULLNAME_AR')}
                  value={values?.fullNameAr}
                />
              </InputGroup>
              {errors.fullNameAr && touched.fullNameAr ? (
                <div className='small text-danger'>{errors.fullNameAr}</div>
              ) : null}
              {!checkFullNameUser(values?.fullNameAr) && !errors.fullNameAr && isShowViewModalCreateUser &&
                <div className='small text-danger'>{t('USERS_PAGE.CONTENT_STEP.VALIDATE_STEP.VALIDATE_FULLNAME')}</div>
              }
            </Col>
          </>}
          {/* Email */}
          <Col sm={12} md={6} className='mt-8'>
            <InputGroup className='h-50px'>
              <InputGroup.Text id='basic-addon1'>
                <img src='/assets/images/icons/mail.svg'></img>
              </InputGroup.Text>
              <Field
                className='form-control h-50px'
                name='email'
                type="text"
                autoComplete="off"
                placeholder={values.userType === USER_TYPE.WORKER ? t('USERS_PAGE.CONTENT_STEP.STEP2.EMAIL_REQUIRED') : t('USERS_PAGE.CONTENT_STEP.STEP2.EMAIL')}
                value={values?.email}
              />
            </InputGroup>
            {errors.email && touched.email ? (
              <div className='small text-danger'>{errors.email}</div>
            ) : null}
          </Col>
          {/* Phone */}
          <Col sm={12} md={6} className='mt-8'>
            <InputGroup className='h-50px'>
              <InputGroup.Text id='provider-name'>
                <img src='/assets/images/icons/phone.svg' alt='phone'></img>
              </InputGroup.Text>
              {/* <Field as='select' className='form-phone select-phone-type' name='firstPhoneNumber' onBlur={() => { handlePhoneChange(values, setFieldError) }}>
                <option value="+966">+966</option>
                <option value="+973">+973</option>
              </Field> */}
              <Field
                className='form-control'
                name='phone'
                type="text"
                placeholder={t('CREATE_CUSTOMER_MODAL.PHONE')}
              // onKeyUp={() => { handlePhoneChange(values, setFieldError) }}
              //onBlur={() => { handlePhoneChange(values, setFieldError) }}
              />
            </InputGroup>
            {errors.phone && touched.phone ? (
              <div className='small text-danger'>{errors.phone}</div>
            ) : null}
            {/* {textErrorPhone &&
              < div className='small text-danger'>{textErrorPhone}</div>
            } */}
          </Col>

          {/* Password */}
          <Col sm={12} md={6} className='mt-8'>
            <InputGroup className='h-50px'>
              <InputGroup.Text id='basic-addon1'>
                <img src='/assets/images/icons/password.svg'></img>
              </InputGroup.Text>
              <Field
                className='form-control h-50px'
                name='password'
                type="password"
                autoComplete="off"
                placeholder={values.userType === USER_TYPE.WORKER ? t('USERS_PAGE.CONTENT_STEP.STEP2.PASSWORD_REQUIRED') : t('USERS_PAGE.CONTENT_STEP.STEP2.PASSWORD')}
                value={values?.password}
              />
            </InputGroup>
            {errors.password && touched.password ? (
              <div className='small text-danger'>{errors.password}</div>
            ) : null}
          </Col>



          {/* Worker ID / National ID */}
          {(values.userType === USER_TYPE.WORKER || values.userType === USER_TYPE.DRIVER || values.userType === USER_TYPE.TECHNICIAN) && <Col sm={12} md={6} className='mt-8'>
            <InputGroup className='h-50px'>
              <InputGroup.Text id='basic-addon1'>
                <img src='/assets/images/icons/card.svg' alt='id'></img>
              </InputGroup.Text>
              <Field
                className='form-control h-50px'
                name='workerId'
                type="text"
                placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.NATIONAL_ID')}
                value={values?.workerId}
              />
            </InputGroup>
            {errors.workerId && touched.workerId ? (
              <div className='small text-danger'>{errors.workerId}</div>
            ) : null}
          </Col>}

          {/* City - For Drivers */}
          {(values.userType === USER_TYPE.DRIVER || values.userType === USER_TYPE.TECHNICIAN) && <Col sm={12} md={6} className='mt-8'>
            <label className='form-label'>{t('USERS_PAGE.CONTENT_STEP.STEP2.CITY')}</label>
            <div className={`input-group-with-icon ${rtl ? 'rtl' : ''}`}>
              <div className='input-icon'>
                <img src='/assets/images/icons/globe-hemisphere-east.svg' alt='city'></img>
              </div>
              <CustomSelect
                value={values.cityId || ""}
                onChange={(e) => {
                  setFieldValue('cityId', e.target.value);
                  setFieldValue('housingId', '');
                  if (e.target.value) {
                    dispatch(userActions.fetchHousingByProvinceData(e.target.value));
                  } else {
                    dispatch(userActions.fetchHousingData());
                  }
                }}
                options={[
                  { value: "", label: t('USERS_PAGE.CONTENT_STEP.STEP2.PROVINCE_REQUIRED') },
                  ...(provinces || []).map((province) => ({
                    value: province.id?.toString() || "",
                    label: (rtl ? province.nameAR : province.nameEN) || ''
                  }))
                ]}
                placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.PROVINCE_REQUIRED')}
              />
            </div>
            {errors.cityId && touched.cityId ? (
              <div className='small text-danger'>{errors.cityId}</div>
            ) : null}
          </Col>}

          {/* Nationality */}
          {values.userType === USER_TYPE.WORKER && <Col sm={12} md={6} className='mt-8'>
            <label className='form-label'>{t('USERS_PAGE.CONTENT_STEP.STEP2.NATIONALITY')}</label>
            <div className={`input-group-with-icon ${rtl ? 'rtl' : ''}`}>
              <div className='input-icon'>
                <img src='/assets/images/icons/globe.svg' alt='nationality'></img>
              </div>
              <CustomSelect
                value={values.nationality !== undefined && values.nationality !== null ? values.nationality : ""}
                onChange={(e) => setFieldValue("nationality", e.target.value)}
                options={[
                  { value: "", label: t('USERS_PAGE.CONTENT_STEP.STEP2.NATIONALITY_REQUIRED') },
                  ...(nationalities || []).map((national: Nationality) => ({
                    value: national.id !== undefined && national.id !== null ? national.id : "",
                    label: (rtl ? national?.nameAr : national?.nameEn) || ''
                  }))
                ]}
                placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.NATIONALITY_REQUIRED')}
                maxDropdownHeight="250px"
              />
            </div>
            {errors.nationality && touched.nationality ? (
              <div className='small text-danger'>{errors.nationality}</div>
            ) : null}
          </Col>}

          {/* Province - For Workers */}
          {(values.userType === USER_TYPE.WORKER) && <Col sm={12} md={6} className='mt-8'>
            <label className='form-label'>{t('USERS_PAGE.CONTENT_STEP.STEP2.CITY')}</label>
            <div className={`input-group-with-icon ${rtl ? 'rtl' : ''}`}>
              <div className='input-icon'>
                <img src='/assets/images/icons/globe-hemisphere-east.svg' alt='province'></img>
              </div>
              <CustomSelect
                value={values.cityId || ""}
                onChange={(e) => handleProvinceChange(e.target.value)}
                options={[
                  { value: "", label: t('USERS_PAGE.CONTENT_STEP.STEP2.PROVINCE_REQUIRED') },
                  ...(provinces || []).map((province: Province) => ({
                    value: province.id?.toString() || "",
                    label: (rtl ? province?.nameAR : province?.nameEN) || ''
                  }))
                ]}
                placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.PROVINCE_REQUIRED')}
                maxDropdownHeight="250px"
              />
            </div>
            {errors.cityId && touched.cityId ? (
              <div className='small text-danger'>{errors.cityId}</div>
            ) : null}
          </Col>}

          {/* Housing - For Workers and Drivers */}
          {(values.userType === USER_TYPE.WORKER || values.userType === USER_TYPE.DRIVER || values.userType === USER_TYPE.TECHNICIAN) && <Col sm={12} md={6} className='mt-8'>
            <label className='form-label'>{t('USERS_PAGE.CONTENT_STEP.STEP2.HOUSING')}</label>
            <div className={`input-group-with-icon ${rtl ? 'rtl' : ''}`}>
              <div className='input-icon'>
                <img src='/assets/images/icons/house.svg' alt='housing'></img>
              </div>
              <CustomSelect
                value={values.housingId?.toString() || ""}
                onChange={(e) => setFieldValue("housingId", e.target.value)}
                options={[
                  { value: "", label: t('USERS_PAGE.CONTENT_STEP.STEP2.HOUSING_REQUIRED') },
                  ...getHousingOptions().map((housing: Housing) => ({
                    value: housing.id?.toString() || "",
                    label: (rtl ? (housing.nameAR || housing.nameAr) : (housing.nameEN || housing.nameEn)) || ''
                  }))
                ]}
                placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.HOUSING_REQUIRED')}
                maxDropdownHeight="250px"
                disabled={!values.cityId && !(isInitialLoad && values.housingId)}
              />
            </div>
            {errors.housingId && touched.housingId ? (
              <div className='small text-danger'>{errors.housingId}</div>
            ) : null}
          </Col>}

          {/* Supervisor for Driver */}
          {(values.userType === USER_TYPE.DRIVER || values.userType === USER_TYPE.TECHNICIAN) && <Col sm={12} md={6} className='mt-8'>
            <label className='form-label'>{t('USERS_PAGE.CONTENT_STEP.STEP3.TITLE_ADMIN')}</label>
            <div className={`input-group-with-icon ${rtl ? 'rtl' : ''}`}>
              <div className='input-icon'>
                <img src='/assets/images/icons/service-and-skill.svg' alt='supervisor'></img>
              </div>
              <CustomSelect
                value={values.adminId || ""}
                onChange={(e) => setFieldValue("adminId", e.target.value)}
                options={[
                  { value: "", label: t('USERS_PAGE.CONTENT_STEP.STEP3.SELECT_ADMIN') },
                  ...(listAdmin || []).map((admin: AdminResponse) => ({
                    value: admin.id || "",
                    label: (rtl ? admin.fullNameAr : admin.fullNameEn) || ''
                  }))
                ]}
                placeholder={t('USERS_PAGE.CONTENT_STEP.STEP3.SELECT_ADMIN')}
                maxDropdownHeight="250px"
              />
            </div>
            {errors.adminId && touched.adminId ? (
              <div className='small text-danger'>{errors.adminId}</div>
            ) : null}
          </Col>}

          {/* Bus Capacity for Driver */}
          {(values.userType === USER_TYPE.DRIVER || values.userType === USER_TYPE.TECHNICIAN) && <Col sm={12} md={6} className='mt-8'>
            <label className='form-label'>{t('USERS_PAGE.CONTENT_STEP.STEP3.BUS_CAPACITY')}</label>
            {/* Note: Translation key might need to be added or used existing one. Using hardcoded label if translation missing */}
            <InputGroup className='h-50px'>
              <InputGroup.Text id='basic-addon1'>
                <img src='/assets/images/icons/bus.svg' alt='bus' style={{ width: 20 }}></img>
              </InputGroup.Text>
              <Field
                className='form-control h-50px'
                name='capacity'
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="20"
                value={values?.capacity !== null && values?.capacity !== undefined ? String(values.capacity) : ''}
                onChange={(e: any) => {
                  const val = e.target.value;
                  // Allow empty to clear, otherwise accept integers including 0
                  if (val === '' || /^[0-9]+$/.test(val)) {
                    setFieldValue('capacity', val === '' ? '' : Number(val));
                  }
                }}
              />
            </InputGroup>
            {errors.capacity && touched.capacity ? (
              <div className='small text-danger'>{errors.capacity}</div>
            ) : null}
          </Col>}
          {/* Confirm Password */}
          {/* <Col sm={12} md={6} className='mt-8'>
            <InputGroup className='h-50px'>
              <InputGroup.Text id='basic-addon1'>
                <img src='/assets/images/icons/password.svg'></img>
              </InputGroup.Text>
              <Field
                className='form-control h-50px'
                name='confirmPassword'
                type="password"
                autoComplete="off"
                placeholder={t('USERS_PAGE.CONTENT_STEP.STEP2.CONFIRM_PASSWORD')}
                value={values?.confirmPassword}
              />
            </InputGroup>
            {errors.confirmPassword && touched.confirmPassword ? (
              <div className='small text-danger'>{errors.confirmPassword}</div>
            ) : null}
          </Col> */}
          {(values.userType !== USER_TYPE.WORKER) &&
            <Col sm={12} className='mt-8'>
              <div className='pb-4'>
                <label className='form-check form-switch form-switch-sm form-check-custom form-check-solid select-toggle' style={{ width: 'max-content' }}>
                  <input className='form-check-input  mx-2' type='checkbox' checked={isActive} onChange={handleChangeStatus} />
                  <span className='form-check-label text-gray-700 fs-6 fw-bold ms-0 me-2'>{t('USERS_PAGE.CONTENT_STEP.STEP4.ACTIVE')}</span>
                </label>
              </div>
            </Col>
          }
        </Row>
      </div>
    </>
  )
}


export default AccountInfo;